import clsx from "clsx";
import { Loader2 } from "lucide-react";
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md";

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
}

const VARIANTS: Record<Variant, string> = {
  // Primary com gradient cyan + glow — igual ao landing site (cor fixa: dark
  // não muda porque o fundo é gradient ciano puro)
  primary:
    "bg-gradient-to-br from-[#22D3EE] to-[#67E8F9] text-slate-900 font-semibold shadow-glow " +
    "hover:shadow-glow-hover hover:-translate-y-px active:translate-y-0 " +
    "disabled:from-brand-200 disabled:to-brand-100 disabled:text-slate-500 disabled:shadow-none disabled:transform-none",
  secondary:
    "bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 " +
    "border border-surface-300 dark:border-slate-700 " +
    "hover:bg-surface-50 dark:hover:bg-slate-700 hover:-translate-y-px active:translate-y-0 " +
    "disabled:opacity-50",
  ghost:
    "bg-transparent text-slate-700 dark:text-slate-300 " +
    "hover:bg-surface-100 dark:hover:bg-slate-800 active:scale-[0.98] disabled:opacity-50",
  danger:
    "bg-red-600 text-white hover:bg-red-700 hover:-translate-y-px active:translate-y-0 disabled:bg-red-300",
};

const SIZES: Record<Size, string> = {
  sm: "h-8 px-3 text-sm",
  md: "h-10 px-4 text-sm",
};

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  {
    variant = "primary",
    size = "md",
    loading = false,
    disabled,
    icon,
    children,
    className,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={rest.type ?? "button"}
      disabled={disabled || loading}
      className={clsx(
        "inline-flex items-center justify-center gap-2 rounded-lg font-medium",
        "transition-all duration-150 ease-out", // colors + transform + shadow
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-slate-900",
        "disabled:cursor-not-allowed",
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : icon}
      {children}
    </button>
  );
});
